import React from 'react';
import { defaultMemoize } from 'reselect';
import { Messages } from '../../../../../gen-src/Localize';
import { SelectionProviderChildProps } from '../../../data-catalog-shared';
import { LineageItemType } from '../lineage/LineageItemType';
import { LineageItemConfig, LineageSection, LineageSectionType } from '../placePage/LineageConfiguration';
import { PaneWithTitle } from '../placePage/PaneWithTitle';
import { DashboardsGridSortProvider } from '../sorting/sortProviders/DashboardsGridSortProvider';
import { DatabasesGridSortProvider } from '../sorting/sortProviders/DatabasesGridSortProvider';
import { DatasourcesGridSortProvider } from '../sorting/sortProviders/DatasourcesGridSortProvider';
import { FieldsGridSortProvider } from '../sorting/sortProviders/FieldsGridSortProvider';
import { FlowsGridSortProvider } from '../sorting/sortProviders/FlowsGridSortProvider';
import { MetricsGridSortProvider } from '../sorting/sortProviders/MetricsGridSortProvider';
import { TablesGridSortProvider } from '../sorting/sortProviders/TablesGridSortProvider';
import { VirtualConnectionTablesGridSortProvider } from '../sorting/sortProviders/VirtualConnectionTablesGridSortProvider';
import { VirtualConnectionsGridSortProvider } from '../sorting/sortProviders/VirtualConnectionsGridSortProvider';
import { WorkbooksGridSortProvider } from '../sorting/sortProviders/WorkbooksGridSortProvider';
import { LensDownstreamDashboardsQueryGrid } from './LensDownstreamDashboardsQueryGrid';
import { LensDownstreamMetricsQueryGrid } from './LensDownstreamMetricsQueryGrid';
import { LensDownstreamOwnersPane } from './LensDownstreamOwnersPane';
import { LensDownstreamWorkbooksQueryGrid } from './LensDownstreamWorkbooksQueryGrid';
import { LensFieldsQueryGrid } from './LensFieldsQueryGrid';
import { LensLineageCounts } from './LensLineageCountsQueryWrapper';
import { LensUpstreamDatabasesQueryGrid } from './LensUpstreamDatabasesQueryGrid';
import { LensUpstreamDatasourcesQueryGrid } from './LensUpstreamDatasourcesQueryGrid';
import { LensUpstreamFlowsQueryGrid } from './LensUpstreamFlowsQueryGrid';
import { LensUpstreamTablesQueryGrid } from './LensUpstreamTablesQueryGrid';
import { LensUpstreamVirtualConnectionTablesQueryGrid } from './LensUpstreamVirtualConnectionTablesQueryGrid';
import { LensUpstreamVirtualConnectionsQueryGrid } from './LensUpstreamVirtualConnectionsQueryGrid';

export type LensLineageItemConfig = LineageItemConfig<LensLineageCounts, LensLineagePaneParams>;
export type LensLineageSection = LineageSection<LensLineageCounts, LensLineagePaneParams>;

export type LensLineagePaneParams = SelectionProviderChildProps & Readonly<{
  title: string,
  vizportalUrlId: string;
  count: number | null;
}>;

const getLensUpstreamItems = (): ReadonlyArray<LensLineageItemConfig> => {
  return [
    {
      lineageItemType: LineageItemType.Database,
      messagesForLineage: { withCount: Messages.DatabasesWithCount, withoutCount: Messages.Databases },
      messagesForPane: { withCount: Messages.UpstreamDatabasesWithCount, withoutCount: Messages.UpstreamDatabases },
      count: counts => counts.upstreamDatabases,
      path: (urls, vizportalUrlId) => urls.lensLineageUpstreamDatabasesPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatabasesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <LensUpstreamDatabasesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </DatabasesGridSortProvider>
        </PaneWithTitle>
      ),
    }, {
      lineageItemType: LineageItemType.Table,
      messagesForLineage: { withCount: Messages.TablesWithCount, withoutCount: Messages.Tables },
      messagesForPane: { withCount: Messages.UpstreamTablesWithCount, withoutCount: Messages.UpstreamTables },
      count: counts => counts.upstreamTables,
      path: (urls, vizportalUrlId) => urls.lensLineageUpstreamTablesPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <TablesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <LensUpstreamTablesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </TablesGridSortProvider>
        </PaneWithTitle>
      ),
    }, {
      lineageItemType: LineageItemType.VirtualConnection,
      messagesForLineage: { withCount: Messages.VirtualConnectionsWithCount, withoutCount: Messages.VirtualConnections },
      messagesForPane: { withCount: Messages.UpstreamVirtualConnectionsWithCount, withoutCount: Messages.UpstreamVirtualConnections },
      count: counts => counts.upstreamVirtualConnections,
      path: (urls, vizportalUrlId) => urls.lensLineageUpstreamVirtualConnectionsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <VirtualConnectionsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <LensUpstreamVirtualConnectionsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </VirtualConnectionsGridSortProvider>
        </PaneWithTitle>
      ),
    }, {
      lineageItemType: LineageItemType.VirtualConnectionTable,
      messagesForLineage: { withCount: Messages.VirtualConnectionTablesWithCount, withoutCount: Messages.VirtualConnectionTables },
      messagesForPane: {
        withCount: Messages.UpstreamVirtualConnectionTablesWithCount,
        withoutCount: Messages.UpstreamVirtualConnectionTables
      },
      count: counts => counts.upstreamVirtualConnectionTables,
      path: (urls, vizportalUrlId) => urls.lensLineageUpstreamVirtualConnectionTablesPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <VirtualConnectionTablesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <LensUpstreamVirtualConnectionTablesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </VirtualConnectionTablesGridSortProvider>
        </PaneWithTitle>
      ),
    }, {
      lineageItemType: LineageItemType.Flow,
      messagesForLineage: { withCount: Messages.FlowsWithCount, withoutCount: Messages.Flows },
      messagesForPane: { withCount: Messages.UpstreamFlowsWithCount, withoutCount: Messages.UpstreamFlows },
      count: counts => counts.upstreamFlows,
      path: (urls, vizportalUrlId) => urls.lensLineageUpstreamFlowsPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <FlowsGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <LensUpstreamFlowsQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />
            }
          </FlowsGridSortProvider>
        </PaneWithTitle>
      ),
    }, {
      lineageItemType: LineageItemType.Datasource,
      messagesForLineage: { withCount: Messages.DatasourcesWithCount, withoutCount: Messages.Datasources },
      messagesForPane: { withCount: Messages.UpstreamDatasourcesWithCount, withoutCount: Messages.UpstreamDatasources },
      count: counts => counts.upstreamDatasources,
      path: (urls, vizportalUrlId) => urls.lensLineageUpstreamDatasourcesPath(vizportalUrlId),
      paneFactory: (paneParams) => (
        <PaneWithTitle title={paneParams.title}>
          <DatasourcesGridSortProvider>
            {({ orderBy, updateSortOrder }) =>
              <LensUpstreamDatasourcesQueryGrid
                vizportalUrlId={paneParams.vizportalUrlId}
                rowCount={paneParams.count}
                orderBy={orderBy}
                updateSortOrder={updateSortOrder}
              />}
          </DatasourcesGridSortProvider>
        </PaneWithTitle>
      ),
    },
  ];
};
const memoizedGetLensUpstreamItems = defaultMemoize(getLensUpstreamItems);

const LensAnchor: LensLineageItemConfig = {
  lineageItemType: LineageItemType.Lens,
  messagesForLineage: { withCount: Messages.FieldsWithCount, withoutCount: Messages.Fields },
  messagesForPane: { withCount: Messages.FieldsWithCount, withoutCount: Messages.Fields },
  count: counts => counts.fields,
  path: (urls, vizportalUrlId) => urls.lensLineageFieldsPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <PaneWithTitle title={paneParams.title}>
      <FieldsGridSortProvider>
        {({ orderBy, updateSortOrder }) =>
          <LensFieldsQueryGrid
            vizportalUrlId={paneParams.vizportalUrlId}
            orderBy={orderBy}
            updateSortOrder={updateSortOrder}
            rowCount={paneParams.count} />}
      </FieldsGridSortProvider>
    </PaneWithTitle>
  ),
};

const getLensDownstreamItems = (): ReadonlyArray<LensLineageItemConfig> => {
  return [{
    lineageItemType: LineageItemType.Dashboard,
    messagesForLineage: { withCount: Messages.DashboardsWithCount, withoutCount: Messages.Dashboards },
    messagesForPane: { withCount: Messages.DownstreamDashboardsWithCount, withoutCount: Messages.DownstreamDashboards },
    count: counts => counts.downstreamDashboards,
    path: (urls, vizportalUrlId) => urls.lensLineageDownstreamDashboardsPath(vizportalUrlId),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <DashboardsGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <LensDownstreamDashboardsQueryGrid
              vizportalUrlId={paneParams.vizportalUrlId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />}
        </DashboardsGridSortProvider>
      </PaneWithTitle>
    ),
  },
  {
    lineageItemType: LineageItemType.Workbook,
    messagesForLineage: { withCount: Messages.WorkbooksWithCount, withoutCount: Messages.Workbooks },
    messagesForPane: { withCount: Messages.DownstreamWorkbooksWithCount, withoutCount: Messages.DownstreamWorkbooks },
    count: counts => counts.downstreamWorkbooks,
    path: (urls, vizportalUrlId) => urls.lensLineageDownstreamWorkbooksPath(vizportalUrlId),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <WorkbooksGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <LensDownstreamWorkbooksQueryGrid
              vizportalUrlId={paneParams.vizportalUrlId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />}
        </WorkbooksGridSortProvider>
      </PaneWithTitle>
    ),
  },
  {
    lineageItemType: LineageItemType.Metric,
    messagesForLineage: { withCount: Messages.MetricsWithCount, withoutCount: Messages.Metrics },
    messagesForPane: { withCount: Messages.DownstreamMetricsWithCount, withoutCount: Messages.DownstreamMetrics },
    count: counts => counts.downstreamMetrics,
    path: (urls, vizportalUrlId) => urls.lensLineageDownstreamMetricsPath(vizportalUrlId),
    paneFactory: (paneParams) => (
      <PaneWithTitle title={paneParams.title}>
        <MetricsGridSortProvider>
          {({ orderBy, updateSortOrder }) =>
            <LensDownstreamMetricsQueryGrid
              vizportalUrlId={paneParams.vizportalUrlId}
              rowCount={paneParams.count}
              orderBy={orderBy}
              updateSortOrder={updateSortOrder}
            />}
        </MetricsGridSortProvider>
      </PaneWithTitle>
    ),
  }
  ];
};
const memoizedGetLensDownstreamItems = defaultMemoize(getLensDownstreamItems);

const LensOwners: LensLineageItemConfig = {
  lineageItemType: LineageItemType.User,
  messagesForLineage: { withCount: Messages.OwnersWithCount, withoutCount: Messages.Owners },
  messagesForPane: { withCount: Messages.DownstreamOwnersWithCount, withoutCount: Messages.DownstreamOwners },
  count: counts => counts.downstreamOwners,
  path: (urls, vizportalUrlId) => urls.lensLineageDownstreamOwnersPath(vizportalUrlId),
  paneFactory: (paneParams) => (
    <LensDownstreamOwnersPane
      title={paneParams.title}
      vizportalUrlId={paneParams.vizportalUrlId}
      rowCount={paneParams.count}
    />
  ),
};

const getLensLineageConfiguration = (): ReadonlyArray<LensLineageSection> => {
  return [
    {
      sectionType: LineageSectionType.Group,
      key: 'upstream',
      items: memoizedGetLensUpstreamItems(),
    },
    {
      sectionType: LineageSectionType.Anchor,
      key: 'anchor',
      items: [LensAnchor],
    },
    {
      sectionType: LineageSectionType.Group,
      key: 'downstream',
      items: memoizedGetLensDownstreamItems(),
    },
    {
      sectionType: LineageSectionType.Owners,
      key: 'owners',
      items: [LensOwners],
    },
  ];
};
export const memoizedGetLensLineageConfiguration = defaultMemoize(getLensLineageConfiguration);
